import { streetPart, battleStoreStreets } from '../../../../utils/storeUtils';
import React from 'react';
import { Swords, Crown, Trophy, Zap, ArrowRightLeft, Info, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../lib/store';
import { CURRENCIES } from '../../../../constants';
import { battleItemValue, summarizeBattleItems, type ScorableBattleItem } from '../../../lib/cartAssistant/battleScoring';

interface BattleItem extends ScorableBattleItem {
  canonicalName: string;
  priceA: number;
  priceB: number;
  unitAnalysis?: {
    isSameSize: boolean;
    normalizedRatio: number;
    unitMessage: string;
  };
}

interface StoreBattleCardProps {
  storeA: string;
  storeB: string;
  storeAAddresses?: string[];
  storeBAddresses?: string[];
  winner: string;
  savings: number;
  itemCount: number;
  totalA?: number;
  totalB?: number;
  sizeAdjusted?: boolean;
  excludedCount?: number;
  commonItems: BattleItem[];
  activeCurrencySymbol?: string;
  // The store the user actually bought at. When it isn't the winner, the
  // card shows what they overpaid instead of the winner's advantage.
  shoppedAt?: string;
}

export const StoreBattleCard: React.FC<{ props: StoreBattleCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  const { userProfile, currency, analytics } = useStore();
  const { 
    storeA, 
    storeB,
    storeAAddresses,
    storeBAddresses,
    commonItems = [],
    activeCurrencySymbol: propCurrencySymbol,
    shoppedAt
  } = props || {};

  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find(c => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || propCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, propCurrencySymbol]);

  if (!storeA || !storeB) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-xl text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('chat.preparing_battle')}...</p>
        </div>
      );
    }
    return null;
  }

  const addressesFor = (name: string, supplied?: string[]) => {
    if (supplied?.length) return [...new Set(supplied.filter(Boolean).map(streetPart))];
    const embedded = name.split(',').slice(1).join(',').trim();
    if (/^\d/.test(embedded)) return [streetPart(embedded)];
    // Older cards can use recorded addresses already loaded in the app.
    return battleStoreStreets(analytics?.item_data, [name]);
  };
  const addressesA = addressesFor(storeA, storeAAddresses);
  const addressesB = addressesFor(storeB, storeBAddresses);
  const fallback = summarizeBattleItems(commonItems);
  const hasServerTotals = props.totalA != null && props.totalB != null;
  const totalA = hasServerTotals ? props.totalA! : fallback.totalA;
  const totalB = hasServerTotals ? props.totalB! : fallback.totalB;
  const savings = Math.abs(totalA - totalB);
  const winner = savings < 0.005 ? 'Tie' : totalA < totalB ? storeA : storeB;
  const itemCount = hasServerTotals ? (props.itemCount ?? fallback.itemCount) : fallback.itemCount;
  const sizeAdjusted = props.sizeAdjusted ?? fallback.sizeAdjusted;
  const excludedCount = props.excludedCount ?? fallback.excludedCount;

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Scoreboard Header */}
      <div className="bg-[#1a2b3c] p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent opacity-50" />
        
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-1.5 mb-6">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
               <Swords size={12} className="text-white" />
            </div>
            <span className="text-[9px] font-black uppercase tracking-[0.3em] text-emerald-400">{t('landing.battle_report')}</span>
          </div>

          {sizeAdjusted && (
            <p className="text-[9px] font-bold text-slate-300 text-center mb-4">
              {t('comparison.size_adjusted_note', { defaultValue: 'Totals compare the same package sizes' })}
            </p>
          )}
          <div className="flex items-center justify-between gap-4 mb-6">
            {/* Store A */}
            <div className={`flex-1 text-center space-y-1 transition-all min-w-0 ${winner === storeA ? 'scale-105' : ''}`}>
               {winner === storeA && <Crown size={18} className="text-amber-400 mx-auto mb-1" />}
               <div className="font-black text-[10px] uppercase tracking-tight break-words px-1">{storeA.split(',')[0]}</div>
               <div className="text-[9px] font-medium leading-snug text-slate-300 normal-case break-words px-1">
                 {addressesA.length ? addressesA.map(address => <div key={address}>{address}</div>) : t('common.unknown_address', { defaultValue: 'Unknown Address' })}
               </div>
               <div className="text-2xl font-black tracking-tighter">{activeCurrencySymbol}{totalA.toFixed(2)}</div>
               {winner === storeA && (
                  <div className="inline-block px-2 py-0.5 bg-emerald-500 rounded-full text-[7px] font-black uppercase mt-1 text-white">{t('comparison.champion')}</div>
               )}
            </div>

            <div className="flex flex-col items-center shrink-0">
               <div className="w-px h-4 bg-white/10" />
               <div className="my-1.5 w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center font-black text-[7px] text-gray-500">{t('landing.vs')}</div>
               <div className="w-px h-4 bg-white/10" />
            </div>

            {/* Store B */}
            <div className={`flex-1 text-center space-y-1 transition-all min-w-0 ${winner === storeB ? 'scale-105' : ''}`}>
               {winner === storeB && <Crown size={18} className="text-amber-400 mx-auto mb-1" />}
               <div className="font-black text-[10px] uppercase tracking-tight break-words px-1">{storeB.split(',')[0]}</div>
               <div className="text-[9px] font-medium leading-snug text-slate-300 normal-case break-words px-1">
                 {addressesB.length ? addressesB.map(address => <div key={address}>{address}</div>) : t('common.unknown_address', { defaultValue: 'Unknown Address' })}
               </div>
               <div className="text-2xl font-black tracking-tighter">{activeCurrencySymbol}{totalB.toFixed(2)}</div>
               {winner === storeB && (
                  <div className="inline-block px-2 py-0.5 bg-emerald-500 rounded-full text-[7px] font-black uppercase mt-1 text-white">{t('comparison.champion')}</div>
               )}
            </div>
          </div>

          {shoppedAt && shoppedAt !== winner ? (
            <div className="w-full bg-rose-500/10 rounded-2xl p-2.5 border border-rose-500/20 flex items-center justify-center gap-2">
               <TrendingUp size={12} className="text-rose-400 shrink-0" />
               <p className="text-[9px] font-black text-rose-400 uppercase tracking-widest text-center">
                 {t('chat.overpaid_count', { amount: `${activeCurrencySymbol}${savings.toFixed(2)}`, count: itemCount })}
               </p>
            </div>
          ) : (
            <div className="w-full bg-white/5 rounded-2xl p-2.5 border border-white/5 flex items-center justify-center gap-2">
               <Zap size={12} className="text-emerald-400 shrink-0" />
               <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest text-center">
                 {t('chat.advantage_save_count', { amount: `${activeCurrencySymbol}${savings.toFixed(2)}`, count: itemCount })}
               </p>
            </div>
          )}
          {excludedCount > 0 && (
            <p className="mt-3 text-[9px] text-slate-300 text-center">
              {t('comparison.unscored_items', { count: excludedCount, defaultValue: "{{count}} matched items not scored: package sizes can't be compared" })}
            </p>
          )}
        </div>
        <Trophy className="absolute -left-6 -bottom-6 text-white/5 rotate-12" size={100} />
      </div>

      {/* Item List */}
      <div className="p-6 space-y-3 max-h-64 overflow-y-auto scrollbar-hide">
        <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest px-1">{t('chat.price_breakdown')}</div>
        {commonItems.map((item, idx) => {
          const adjustedA = battleItemValue(item);
          const difference = adjustedA === null ? 0 : adjustedA - item.priceB;
          const tied = Math.abs(difference) < 0.005;
          const valueAtA = !tied && difference < 0;
          const valueAtB = !tied && difference > 0;
          const differentSizes = item.unitAnalysis?.isSameSize === false;
          return (
            <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group">
              <div className="min-w-0 flex-1 pr-4">
                <p className="text-[11px] font-black text-[#1a2b3c] truncate leading-tight">{item.canonicalName}</p>
                {item.unitAnalysis && !item.unitAnalysis.isSameSize && (
                  <p className="text-[8px] font-bold text-indigo-500 uppercase mt-0.5">
                    {item.unitAnalysis.unitMessage}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <div className={`px-2 py-1 rounded-lg text-center min-w-[50px] border ${valueAtA ? 'bg-emerald-50 border-emerald-500/20 text-emerald-600' : 'bg-white border-transparent text-gray-400'}`}>
                  <span className="text-[10px] font-black">{activeCurrencySymbol}{(item.priceA || 0).toFixed(2)}</span>
                  {valueAtA && differentSizes && <div className="text-[7px] font-black uppercase">{t('comparison.better_value', { defaultValue: 'Better Value' })}</div>}
                </div>
                <ArrowRightLeft size={8} className="text-gray-300" />
                <div className={`px-2 py-1 rounded-lg text-center min-w-[50px] border ${valueAtB ? 'bg-emerald-50 border-emerald-500/20 text-emerald-600' : 'bg-white border-transparent text-gray-400'}`}>
                  <span className="text-[10px] font-black">{activeCurrencySymbol}{(item.priceB || 0).toFixed(2)}</span>
                  {valueAtB && differentSizes && <div className="text-[7px] font-black uppercase">{t('comparison.better_value', { defaultValue: 'Better Value' })}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
